import { setBaseUrl } from "../../common/jsonRpcRoutes";

/**
 * Tag names of every standalone MFE web component that can redirect the guest
 * to Stripe/FreedomPay and be redirected back to (as opposed to being purely
 * embedded inside the full `<guest-checkout>` app).
 */
export const MFE_ELEMENT_TAGS = [
  "secure-your-booking",
  "booking-form",
  "manage-your-order",
  "confirm-payment",
] as const;

/**
 * Returns true when the current page is hosting one of the standalone MFE
 * web components (as opposed to the full `<guest-checkout>` app).
 */
export function isMfeEmbedded(): boolean {
  return document.querySelector(MFE_ELEMENT_TAGS.join(",")) !== null;
}

/**
 * Returns true when the current page URL contains Stripe's post-payment
 * redirect parameters, indicating that Stripe has redirected back to the
 * embedding page after the guest completed (or abandoned) payment.
 */
export function isStripeRedirect(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.has("payment_intent") || params.has("setup_intent");
}

/** Query params Stripe appends to its post-payment redirect (see stripeConnectForm.tsx). */
export const STRIPE_REDIRECT_PARAMS = [
  "payment_intent",
  "payment_intent_client_secret",
  "setup_intent",
  "setup_intent_client_secret",
  "redirect_status",
  "action",
  "pending_booking_id",
  "booking_id",
] as const;

/**
 * Strips Stripe's post-payment redirect params from a query string so a
 * freshly mounted flow starts from the host page's ordinary parameters rather
 * than from the payment result.
 */
export function stripStripeRedirectParams(search: string): string {
  const params = new URLSearchParams(search);
  for (const key of STRIPE_REDIRECT_PARAMS) {
    params.delete(key);
  }
  const cleaned = params.toString();
  return cleaned ? `?${cleaned}` : "";
}

/**
 * Reload the host page without Stripe's result parameters so its originating
 * element mounts the step the guest paid from. This keeps embedded retries off
 * partner routes such as `/book`, which the host site need not serve.
 */
export function retryWithinEmbeddedElement(route: string, urlParams: URLSearchParams): void {
  const retryParams = new URLSearchParams(urlParams);
  for (const key of STRIPE_REDIRECT_PARAMS) {
    if (key !== "booking_id" || route === "book") retryParams.delete(key);
  }
  const cleaned = retryParams.toString();
  const search = cleaned ? `?${cleaned}` : "";
  window.location.href = `${window.location.pathname}${search}${window.location.hash}`;
}

/**
 * Props shared by every standalone MFE web component element.
 */
export interface BaseMfeElementProps {
  readonly bookingId: string;
  readonly signature: string;
  readonly baseUrl: string;
  /**
   * CSS custom property overrides for venue theming.
   *
   * Pass a plain CSS string containing `--gc-*` variable declarations.
   * These cascade into all child components, allowing venues to customise
   * colours, fonts, and layout without touching internal styles.
   *
   * Supported variables (with their defaults):
   *   --gc-color-primary          #007A7A   Main CTA colour
   *   --gc-color-primary-hover    #006666   Hover state of primary buttons
   *   --gc-color-primary-active   #005555   Pressed/active state
   *   --gc-font-base              "Mulish", sans-serif
   *   --gc-font-heading           "Montserrat", sans-serif
   *   --gc-font-size-body         16px
   *   --gc-border-radius-card     16px
   *   --gc-border-radius-button   12px
   *   --gc-color-bg-page          #E8EAEC
   *   --gc-color-bg-surface       #F9F9FA
   *   --gc-color-bg-white         #ffffff
   *   --gc-color-border           #D8DBDF
   *   --gc-color-success          #28a745
   *   --gc-color-error            #dc3545
   *   --gc-color-warning          #F57C00
   *   --gc-logo-height            40px
   *
   * Example (HTML attribute):
   *   style="--gc-color-primary: #C0392B; --gc-border-radius-button: 4px;"
   */
  readonly theme?: string;
}

/**
 * Sets the base URL synchronously before any child renders (and therefore
 * before SignatureGuard fires its first RPC call). A useEffect would run after
 * the first render, causing the validate-signature request to be sent to the
 * embedding page's origin instead of the Guest Checkout server.
 */
export function applyBaseUrl(baseUrl: string): void {
  if (baseUrl) {
    setBaseUrl(baseUrl);
  }
}

/**
 * Reflects query-string values onto attributes of any matching custom elements
 * in the document, without overwriting attributes that are already set.
 *
 * `attributeParams` maps each target attribute name to the ordered list of
 * query-parameter names to read (the first present value wins). This allows
 * venues to embed an MFE without a separate inline script — the values are
 * taken from the URL that Collins redirects to (e.g.
 * `https://venue.com/pay?booking_id=123&signature=abc`).
 */
export function reflectQueryParamsToAttributes(
  tagName: string,
  attributeParams: Readonly<Record<string, readonly string[]>>,
): void {
  const elements = document.querySelectorAll<HTMLElement>(tagName);
  if (elements.length === 0) return;

  const params = new URLSearchParams(window.location.search);

  for (const el of elements) {
    for (const [attribute, paramNames] of Object.entries(attributeParams)) {
      const value = paramNames.reduce<string | null>((found, name) => found ?? params.get(name), null);
      if (value && !el.getAttribute(attribute)) {
        el.setAttribute(attribute, value);
      }
    }
  }
}

/**
 * Runs `fn` once the DOM is ready. If the document is still parsing (e.g. the
 * script tag appears before the custom element tag), defers to
 * DOMContentLoaded; otherwise runs immediately.
 */
export function runOnDomReady(fn: () => void): void {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", fn);
  } else {
    fn();
  }
}
